import {
  Card,
  Container,
  Flex,
  Heading,
  Spinner,
  Text,
} from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonContentWithSidebar } from 'components/common/layout/content-with-sidebar';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { TableProvider } from 'components/common/table/context';
import { GameDataFooter } from 'components/sections/game-data/footer';
import { ImportSettingsPopover } from 'components/sections/game-data/import-settings-popover';
import { GameDataPitches } from 'components/sections/game-data/pitches';
import { GameDataPitchesSidebar } from 'components/sections/game-data/pitches/sidebar';
import { GameDataTeamLogo } from 'components/sections/game-data/team-logo';
import { Breadcrumbs } from 'components/sections/header/breadcrumbs';
import { GameDataContext } from 'contexts/game-data.context';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { LOCAL_GAMETIME_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMlbGameScore } from 'lib_ts/interfaces/mlb-stats-api/i-game';
import { IMlbPitchExt } from 'lib_ts/interfaces/mlb-stats-api/i-pitch';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { paths } from 'routes/paths';
import { MlbStatsService } from 'services/mlb-stats.service';
import useMiscStore from 'stores/use-misc-store';

const COMPONENT_NAME = 'GameDataGame';

export const GameDataGame = () => {
  const navigate = useNavigate();
  const { gamePk } = useParams();
  const { seasonGames, seasonTeams } = useContext(GameDataContext);
  const [pitches, setPitches] = useState<IMlbPitchExt[]>();

  const [score, setScore] = useState<IMlbGameScore>();

  const game = useMemo(() => {
    if (!gamePk) {
      return undefined;
    }

    const gamePkInt = parseInt(gamePk);
    return seasonGames.find((p) => p.gamePk === gamePkInt);
  }, [gamePk, seasonGames]);

  useEffect(() => {
    if (score) {
      // we've already loaded data
      return;
    }

    if (!game) {
      return;
    }

    if (game.home.score !== undefined && game.away.score !== undefined) {
      // score via game data
      setScore({
        teams: {
          home: {
            runs: game.home.score,
            isWinner: !!game.home.isWinner,
          },
          away: {
            runs: game.away.score,
            isWinner: !!game.away.isWinner,
          },
        },
      });
      return;
    }

    // fallback to score via API
    MlbStatsService.getInstance()
      .getGameScore(game.gamePk)
      .then((result) => setScore(result));
  }, [game, score]);

  const homeTeam = useMemo(
    () => seasonTeams.find((m) => m.teamPk === game?.home.teamPk),
    [seasonTeams, game]
  );

  const awayTeam = useMemo(
    () => seasonTeams.find((m) => m.teamPk === game?.away.teamPk),
    [seasonTeams, game]
  );

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  const bgColor = isDarkMode ? 'black' : 'white';

  const dateString = useMemo(() => {
    if (!game) {
      return '';
    }

    if (game.gameDate) {
      return StringHelper.getTimezoneString({
        value: game.gameDate,
        format: LOCAL_GAMETIME_FORMAT,
      });
    }

    return game.officialDate;
  }, [game]);

  useEffect(() => {
    if (pitches) {
      // we've already loaded data
      return;
    }

    if (!game) {
      return;
    }

    MlbStatsService.getInstance()
      .getGamePitches(game.gamePk)
      .then((result) => setPitches(result));
  }, [game, pitches]);

  if (!game) {
    return <Spinner size="3" />;
  }

  if (!pitches) {
    return <Spinner size="3" />;
  }

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonContentWithSidebar
        left={
          <FlexTableWrapper
            header={
              <>
                <Breadcrumbs
                  items={[
                    {
                      label: 'common.pitches',
                      onClick: () => navigate(paths.pitches.library),
                    },
                    {
                      label: 'main.game-data',
                      onClick: () => navigate(paths.pitches.gameData.root),
                    },
                    {
                      label: `${game.officialDate}: ${
                        awayTeam?._mx_details?.code ?? game.away.name
                      } @ ${homeTeam?._mx_details?.code ?? game.home.name}`,
                    },
                  ]}
                />

                <Card
                  size="5"
                  className="half-background"
                  style={{
                    background: `linear-gradient(90deg, ${
                      awayTeam?._mx_details?.colorHex ?? bgColor
                    } 0%, ${bgColor} 20%, ${bgColor} 80%, ${
                      homeTeam?._mx_details?.colorHex ?? bgColor
                    } 100%)`,
                  }}
                >
                  <Container size="3">
                    <Flex justify="between" align="center">
                      <Flex gap="2" direction="column" align="center">
                        <GameDataTeamLogo
                          size="60px"
                          teamPK={game.away.teamPk}
                        />

                        <Text size="3">
                          {awayTeam &&
                          awayTeam._mx_details?.code &&
                          awayTeam._mx_details.shortName
                            ? `${awayTeam._mx_details.code} ${awayTeam._mx_details.shortName}`
                            : game.away.name}
                        </Text>
                      </Flex>

                      {score && (
                        <Heading size="8">{score.teams.away.runs}</Heading>
                      )}

                      <Flex
                        mt="5"
                        gap="2"
                        direction="column"
                        align="center"
                        justify="center"
                      >
                        <Text color={RADIX.COLOR.NEUTRAL} align="center">
                          @
                        </Text>
                        <Text
                          color={RADIX.COLOR.NEUTRAL}
                          align="center"
                          truncate
                        >
                          {dateString}
                        </Text>
                        <ImportSettingsPopover />
                      </Flex>

                      {score && (
                        <Heading size="8">{score.teams.home.runs}</Heading>
                      )}

                      <Flex gap="2" direction="column" align="center">
                        <GameDataTeamLogo
                          size="60px"
                          teamPK={game.home.teamPk}
                        />

                        <Text size="3">
                          {homeTeam &&
                          homeTeam._mx_details?.code &&
                          homeTeam._mx_details.shortName
                            ? `${homeTeam._mx_details.code} ${homeTeam._mx_details.shortName}`
                            : game.home.name}
                        </Text>
                      </Flex>
                    </Flex>
                  </Container>
                </Card>
              </>
            }
            table={
              <TableProvider identifier="GDGamePitchesTableProvider">
                <GameDataPitches
                  mode="games"
                  pitches={pitches}
                  playerPk={undefined}
                />
              </TableProvider>
            }
            footer={<GameDataFooter />}
          />
        }
        right={<GameDataPitchesSidebar />}
        vFlex
      />
    </ErrorBoundary>
  );
};
